import { useCallback, useEffect, useMemo, useState } from 'react';
import { parseInstantMs } from './calendar-date';

/**
 * Helpers shared by the two surfaces that start and end a shift.
 *
 * These used to be private to ClockInScreen. The home screen's clock action
 * needs the same ones, and a second copy of any of them is the shape of bug
 * this repo keeps finding: a correct implementation exists and the new call
 * site does not use it. So they moved here rather than being written twice.
 */

/**
 * `HH:MM:SS` from a whole number of seconds. Hours are not wrapped at 24.
 *
 * Absent is an em dash, and that is the signature rather than a caller's
 * concern on purpose. Every render of a duration in this app goes through
 * here, so there is exactly one place that decides what "no answer" looks
 * like, and no call site can accidentally render `00:00:00` for a shift whose
 * start we do not know. Zero is a real duration; not knowing is not.
 */
export function formatElapsed(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds)) return '—';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * The device's position, or null.
 *
 * Resolves rather than rejects on every failure — no permission, no hardware,
 * a timeout — because a clock-in must not be blocked by a location the API
 * treats as optional. The 10s timeout is the point: `getCurrentPosition` is
 * event-based and can call neither callback, and a promise that never settles
 * walks straight past try/catch. Anything awaiting this would hang forever.
 */
export function getGPS(): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 10000, enableHighAccuracy: false },
    );
  });
}

export function useShiftElapsed(clockInAt: string | null | undefined): number | null {
  const startedAt = useMemo(() => parseInstantMs(clockInAt), [clockInAt]);

  const read = useCallback((): number | null => {
    if (startedAt === null) return null;
    const secs = Math.floor((Date.now() - startedAt) / 1000);
    return secs < 0 ? null : secs;
  }, [startedAt]);

  // Lazily initialised so the first paint is already the real figure. Starting
  // at 0 and correcting on the first tick would put a `00:00:00` on screen for
  // up to a second on every mount — the same wrong-number-that-looks-right
  // this hook exists to remove, just briefer.
  const [elapsed, setElapsed] = useState<number | null>(read);

  useEffect(() => {
    setElapsed(read());
    if (startedAt === null) return undefined;
    const id = setInterval(() => setElapsed(read()), 1000);
    return () => clearInterval(id);
  }, [read, startedAt]);

  return elapsed;
}
